import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import InfoIcon from "@mui/icons-material/Info";

// Animation variants
const formVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

const UberDirectConfigForm = ({ existingCredentials, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    client_id: "",
    client_secret: "",
    customer_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Initialize form with existing credentials if available
  useEffect(() => {
    if (existingCredentials) {
      setFormData({
        client_id: existingCredentials.client_id || "",
        client_secret: existingCredentials.client_secret || "",
        customer_id: existingCredentials.customer_id || "",
      });
    } else {
      // Reset form when no credentials exist
      setFormData({
        client_id: "",
        client_secret: "",
        customer_id: "",
      });
    }
  }, [existingCredentials]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await onSubmit(formData);
      setSuccess("Credenciais salvas com sucesso!");

      // Success feedback before closing modal
      setTimeout(() => {
        onCancel(); // Close the form/modal
      }, 1500);
    } catch (error) {
      console.error("Error saving Uber Direct credentials:", error);
      setError(error.message || "Erro ao salvar credenciais");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div variants={formVariants} initial="hidden" animate="visible">
      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Configure sua integração com o Uber Direct fornecendo as credenciais
          da sua conta. Estas informações estão disponíveis no painel de
          desenvolvedores do Uber Direct.
        </Typography>

        <Box
          sx={{
            p: 2,
            bgcolor: "rgba(0, 0, 0, 0.03)",
            borderRadius: 1,
            mb: 3,
            display: "flex",
            alignItems: "flex-start",
          }}
        >
          <InfoIcon sx={{ mr: 1, color: "info.main", mt: 0.3 }} />
          <Typography variant="body2" color="text.secondary">
            Para obter suas credenciais, acesse o{" "}
            <a
              href="https://direct.uber.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Dashboard do Uber Direct
            </a>
            , navegue até a aba "Developer" e crie um novo aplicativo ou use um
            existente.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Client ID"
              name="client_id"
              value={formData.client_id}
              onChange={handleInputChange}
              variant="outlined"
              margin="normal"
              helperText="Identificador único do seu aplicativo Uber Direct"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Client Secret"
              name="client_secret"
              type="password"
              value={formData.client_secret}
              onChange={handleInputChange}
              variant="outlined"
              margin="normal"
              helperText="Chave secreta do seu aplicativo (mantenha em segurança)"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Customer ID"
              name="customer_id"
              value={formData.customer_id}
              onChange={handleInputChange}
              variant="outlined"
              margin="normal"
              helperText="ID do cliente fornecido pelo Uber Direct"
            />
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{
              bgcolor: "#000000",
              "&:hover": { bgcolor: "#333333" },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : existingCredentials ? (
              "Atualizar Credenciais"
            ) : (
              "Salvar Credenciais"
            )}
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
};

export default UberDirectConfigForm;
